import { ApiError } from './errors';

/**
 * Per-mutation toast copy, declared on `useMutation({ meta })`.
 *
 * Every non-GET call has to tell the grower what happened, and doing that at each call site is too
 * easy to forget — several mutations in this app shipped silent before this existed. So the rule is
 * enforced centrally by `<MutationToaster>`, which watches the mutation cache and toasts on every
 * settle; this meta only supplies the wording.
 *
 * - `successMessage` — shown on success. A **function** receives the mutation's own variables, for
 *   copy that names what was submitted ("Turn On command queued successfully"). Omitting it does
 *   **not** buy silence any more: a 2xx on a non-GET always announces itself, falling back to
 *   {@link GENERIC_MUTATION_SUCCESS}. Opt out only via `suppressToast`.
 * - `errorMessage` — overrides the server's own message. Usually leave it out: ADR-0008 says the UI
 *   should prefer `error.message`, which is what the fallback does.
 * - `suppressErrorToast` — keeps the central *success* toast but lets the caller present the
 *   failure itself. The auth screens use it: `useLockoutNotice` already owns the lockout toast, so
 *   a second generic one would double-notify (US-112329).
 * - `suppressToast` — opts a mutation out of both halves. Reserved for writes the grower never
 *   asked for (a background mark-as-read) or probe-shaped POSTs fired repeatedly (boundary
 *   validation), where a toast per call would be noise rather than feedback.
 */
export type MutationToastMeta = {
  /** `never` in the function form so any concretely-typed resolver is assignable; the toaster
   *  supplies the real variables. */
  successMessage?: string | ((variables: never) => string);
  errorMessage?: string;
  suppressErrorToast?: boolean;
  suppressToast?: boolean;
};

// Types `meta` on every `useMutation` call in the app, so a typo in a key is a compile error
// rather than a toast that silently never appears.
declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: MutationToastMeta;
  }
}

/** Last-resort copy when the failure carries no message of its own. */
export const GENERIC_MUTATION_ERROR = 'Something went wrong. Please try again.';

/** Last-resort copy for a 2xx that declared no wording. Prefer a real `meta.successMessage`. */
export const GENERIC_MUTATION_SUCCESS = 'Success';

/** The two tones a failed request may take. See {@link mutationErrorTone}. */
export type ErrorToastTone = 'error' | 'warning';

/**
 * Which tone a failed request gets.
 *
 * `400` is the only status the grower can act on directly — the request itself was malformed or
 * rejected on its contents — so it is the only one shown in the red/error tone. Everything else
 * (401 bad credentials, 404 unregistered phone, 409, 422, 5xx, a dropped connection) is a
 * warning-toned amber toast: still a failure, but not the grower having typed something invalid
 * into this form.
 */
export function mutationErrorTone(error: unknown): ErrorToastTone {
  return error instanceof ApiError && error.status === 400 ? 'error' : 'warning';
}

/**
 * The message to show for a failed mutation: the server's own wording where there is any (per
 * ADR-0008's `{ error: { code, message } }` envelope), else a generic line. A bare network failure
 * has no server message, and axios's "Network Error" is not worth showing a grower.
 */
export function mutationErrorMessage(error: unknown): string {
  if (error instanceof ApiError && error.message && error.status !== 0) {
    return error.message;
  }
  if (error instanceof Error && error.message && error.message !== 'Network Error') {
    return error.message;
  }
  return GENERIC_MUTATION_ERROR;
}

/**
 * The message to announce for a successful mutation, resolving the function form of
 * `meta.successMessage` against the variables the mutation actually ran with.
 *
 * The single cast is the price of `Register['mutationMeta']` being one non-generic type for every
 * mutation in the app: the resolver is declared over `never` so call sites can type their own
 * variables, and only this function knows the real ones.
 */
export function mutationSuccessMessage(
  meta: MutationToastMeta | undefined,
  variables: unknown,
): string {
  const copy = meta?.successMessage;
  if (typeof copy === 'function') {
    return (copy as (input: unknown) => string)(variables);
  }
  return copy ?? GENERIC_MUTATION_SUCCESS;
}
